import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Chart } from 'chart.js';
import { ChartRendererSupabase } from '../charts/ChartRendererSupabase';
import { PolicyImpactResults, PolicyScenario } from '../../services/policyImpactService';
import { generatePolicyTimeSeriesData, TimeSeriesEmissionData, applyPolicyTransformations } from '../../services/policyTimeSeriesService';
import { getCachedBaseline } from '../../services/policyBaselineCache';
import './PolicyImpactChart.css';
import { supabaseClient } from '../../services/supabase';

// Register a global plugin for vertical separator line
const verticalSeparatorPlugin = {
  id: 'verticalSeparator',
  afterDraw: (chart: any) => {
    // Only draw on charts that explicitly opt-in (Policy Tool charts)
    if (chart?.options?.plugins?.verticalSeparator?.enabled !== true) return;
    drawVerticalSeparator(chart);
    drawTargetLine(chart);
  },
};

// Register the plugin
Chart.register(verticalSeparatorPlugin);

interface PolicyImpactChartProps {
  results: PolicyImpactResults | null;
  loading: boolean;
  scenario: PolicyScenario;
  location: string;
  chartType?: 'line' | 'contributions' | 'forecast' | 'stacked';
  targetReductionPercent?: number;
  targetBaselineYear?: number;
  emfacVersion?: '2021' | '2025';
  onTotalsChange?: (totals: {
    baselineYear: number;
    baselineTotal: number;
    projectedYear: number;
    projectedWithPoliciesTotal: number;
    totalReduction: number;
    percentReduction: number;
  }) => void;
}

// Cache for time series data to avoid redundant calculations
const timeSeriesCache = new Map<string, {
  baselineData: TimeSeriesEmissionData[];
  projectedData: TimeSeriesEmissionData[];
}>();

// Create a stable key from scenario and location
function createScenarioKey(location: string, scenario: PolicyScenario, emfacVersion: '2021' | '2025' = '2025'): string {
  return `${location}:${emfacVersion}:${scenario.evAdoptionPercent}:${scenario.vmtReductionPercent}:${scenario.energyEfficiencyPercent || 0}:${scenario.heatPumpAdoptionPercent}:${(scenario as any).gridCleanElectricityYear || 2045}:${scenario.solarCapacityMultiplier}:${scenario.hdvElectrificationPercent}:${(scenario as any).organicsDiversionPercent || 50}:${scenario.vmtReductionComponents?.transitOrientedDevelopment || 0}:${scenario.vmtReductionComponents?.activeTransportation || 0}:${scenario.vmtReductionComponents?.transitImprovements || 0}:${scenario.parkingPricing?.priceIncreasePercent || 0}:${scenario.micromobility?.scalePercent || 0}`;
}

// Helper function to draw vertical separator line at year 2023
function drawVerticalSeparator(chart: any) {
  try {
    // Safety: never draw unless chart opts-in
    if (chart?.options?.plugins?.verticalSeparator?.enabled !== true) return;

    const ctx = chart.ctx;
    const xScale = chart.scales.x;
    const yScale = chart.scales.y;
    
    if (!xScale || !yScale || !ctx) {
      return;
    }
    
    // Find the index of 2023 in the labels array
    const labels = chart.data.labels || [];
    const index2023 = labels.findIndex((label: string | number) => 
      String(label) === '2023' || label === 2023
    );
    
    if (index2023 === -1) {
      return;
    }
    
    // Get pixel position using getPixelForTick (works with category scales)
    let xPosition: number | undefined;
    
    if (xScale.getPixelForTick) {
      // Use getPixelForTick if available (for category scales)
      const tick2023 = xScale.getPixelForTick(index2023);
      if (tick2023 !== undefined && !isNaN(tick2023)) xPosition = tick2023;
    } else {
      // Fallback: use getPixelForValue with label index
      const tick2023 = xScale.getPixelForValue(labels[index2023]);
      if (tick2023 !== undefined && !isNaN(tick2023)) xPosition = tick2023;
    }
    
    // Validate the position is within reasonable bounds (chart width is typically < 2000px)
    if (xPosition !== undefined && !isNaN(xPosition) && xPosition > 0 && xPosition < 10000) {
      // Draw vertical line from top to bottom of chart area
      const topY = yScale.top;
      const bottomY = yScale.bottom;
      
      ctx.save();
      // Solid, dark gray separator at 2023 (subtle but bolder than grid)
      ctx.strokeStyle = 'rgba(55, 65, 81, 0.85)'; // gray-700
      ctx.lineWidth = 1.75;
      ctx.setLineDash([]); // solid
      ctx.lineCap = 'round';
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1.0;
      ctx.beginPath();
      ctx.moveTo(xPosition, topY);
      ctx.lineTo(xPosition, bottomY);
      ctx.stroke();
      ctx.restore();
    }
  } catch (error) {
    console.error('drawVerticalSeparator error:', error);
  }
}

// Helper function to draw the horizontal target line (if present)
function drawTargetLine(chart: any) {
  try {
    // Safety: never draw unless chart opts-in
    if (chart?.options?.plugins?.verticalSeparator?.enabled !== true) return;

    const ctx = chart.ctx;
    const yScale = chart.scales?.y;
    const chartArea = chart.chartArea;

    if (!ctx || !yScale || !chartArea) return;

    const datasets = chart.data?.datasets || [];
    const targetIdx = datasets.findIndex((d: any) => typeof d?.label === 'string' && d.label === 'Target');
    if (targetIdx === -1) return;

    const targetDataset = datasets[targetIdx];
    const data = Array.isArray(targetDataset?.data) ? targetDataset.data : [];

    // Find a sane numeric target value from dataset data
    let targetValue: number | null = null;
    for (let i = 0; i < data.length; i++) {
      const v = data[i];
      if (typeof v === 'number' && Number.isFinite(v)) {
        targetValue = v;
        break;
      }
    }
    if (targetValue === null) return;

    const targetY = yScale.getPixelForValue(targetValue);
    if (!Number.isFinite(targetY)) return;

    // One-time console debugging so we can confirm it's being called + the computed coords
    if (!(chart as any)._targetLineLogged) {
      (chart as any)._targetLineLogged = true;
      // eslint-disable-next-line no-console
      console.log('[PolicyImpactChart] target line debug', {
        label: targetDataset.label,
        targetValue,
        targetY,
        yMin: yScale.min,
        yMax: yScale.max,
        chartArea,
      });
    }

    // Only draw if within chart area (with tolerance)
    // Note: targetY might be slightly outside due to rounding, so use more tolerance
    if (targetY < chartArea.top - 10 || targetY > chartArea.bottom + 10) {
      // eslint-disable-next-line no-console
      console.warn('[PolicyImpactChart] Target line outside chart area:', { 
        targetY, 
        chartAreaTop: chartArea.top, 
        chartAreaBottom: chartArea.bottom, 
        targetValue,
        yMin: yScale.min,
        yMax: yScale.max
      });
      return;
    }

    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = 'rgba(0, 0, 0, 1)';
    // Thinner target line
    ctx.lineWidth = 1.5;
    ctx.setLineDash([12, 6]);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    // Draw from left edge to right edge of chart area (before legend)
    ctx.moveTo(chartArea.left, targetY);
    ctx.lineTo(chartArea.right, targetY);
    ctx.stroke();
    ctx.restore();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('drawTargetLine error:', error);
  }
}

function getNiceAxisMaxAndStep(maxValue: number, desiredTickCount = 6): { max: number; stepSize: number } {
  const max = Number.isFinite(maxValue) ? Math.max(0, maxValue) : 0;
  if (max === 0) return { max: 0, stepSize: 1 };

  const roughStep = max / desiredTickCount;
  const exponent = Math.floor(Math.log10(roughStep));
  const base = Math.pow(10, exponent);
  const fraction = roughStep / base;

  let niceFraction: number;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;

  const stepSize = Math.max(1, Math.round(niceFraction * base));
  const niceMax = Math.ceil(max / stepSize) * stepSize;
  return { max: niceMax, stepSize };
}

const PolicyImpactChart: React.FC<PolicyImpactChartProps> = ({ results, loading, scenario, location, chartType = 'line', targetReductionPercent = 80, targetBaselineYear = 2005, emfacVersion = '2025', onTotalsChange }) => {
  const [timeSeriesData, setTimeSeriesData] = useState<{
    baselineData: TimeSeriesEmissionData[];
    projectedData: TimeSeriesEmissionData[];
  } | null>(null);
  const [timeSeriesLoading, setTimeSeriesLoading] = useState(false);

  // Forecast datasets (BAU vs With Policies) with wedge areas (VMT, EV)
  const [forecastData, setForecastData] = useState<null | {
    years: number[];
    bauTotals: number[];
    withPoliciesTotals: number[];
    vmtWedge: number[];
    evWedge: number[];
  }>(null);

  // Create stable scenario key for memoization
  const scenarioKey = useMemo(() => createScenarioKey(location, scenario, emfacVersion), [location, scenario, emfacVersion]);

  // Track if we're currently generating time series to prevent concurrent calls
  const generatingRef = React.useRef(false);

  // Generate time series data when scenario or location changes
  useEffect(() => {
    const generateTimeSeries = async () => {
      if (!location) return;
      
      // Prevent concurrent generation
      if (generatingRef.current) {
        console.log('PolicyImpactChart: Time series generation already in progress, skipping...');
        return;
      }
      
      // Check old scenario-based cache first (for backward compatibility)
      const cacheKey = scenarioKey;
      if (timeSeriesCache.has(cacheKey)) {
        const cached = timeSeriesCache.get(cacheKey);
        if (cached) {
          setTimeSeriesData(cached);
          return; // Instant return - no loading state, no async operations
        }
      }
      
      // With baseline cache, transformations are instant, so only show loading if baseline doesn't exist
      // Check synchronously to avoid async delays
      // Use cache key that includes EMFAC version
      const baselineCacheKey = `baseline:${location}:emfac${emfacVersion || '2025'}`;
      const baseline = getCachedBaseline(baselineCacheKey);
      
      if (baseline) {
        // We have a cached baseline - apply transformations synchronously (instant)
        try {
          // Apply policy transformations synchronously (instant)
          const transformedProjections = applyPolicyTransformations(baseline, scenario);
          
          // Combine historical (2005-2022) with baseline projections (2023-2045) and transformed projections
          const historicalUpTo2022 = baseline.historicalData.filter((d: TimeSeriesEmissionData) => d.year < 2023);
          const baselineData = [...historicalUpTo2022, ...baseline.baselineProjections];
          const projectedWithPolicies = [...historicalUpTo2022, ...transformedProjections];
          
          const data = {
            baselineData,
            projectedData: projectedWithPolicies,
            location
          };
          
          // Cache the result in old cache for backward compatibility
          timeSeriesCache.set(cacheKey, data);
          // Limit cache size to prevent memory issues
          if (timeSeriesCache.size > 10) {
            const firstKey = timeSeriesCache.keys().next().value;
            if (firstKey) {
              timeSeriesCache.delete(firstKey);
            }
          }
          setTimeSeriesData(data);
        } catch (error) {
          console.error('Error applying policy transformations:', error);
          // Fallback to async method if synchronous transformation fails
          generatingRef.current = true;
          setTimeSeriesLoading(true);
          try {
            const data = await generatePolicyTimeSeriesData(location, scenario, emfacVersion);
            timeSeriesCache.set(cacheKey, data);
            if (timeSeriesCache.size > 10) {
              const firstKey = timeSeriesCache.keys().next().value;
              if (firstKey) {
                timeSeriesCache.delete(firstKey);
              }
            }
            setTimeSeriesData(data);
          } catch (err) {
            console.error('Error generating time series data:', err);
          } finally {
            setTimeSeriesLoading(false);
            generatingRef.current = false;
          }
        }
      } else {
        // No cached baseline - calculate it (expensive, one-time)
        generatingRef.current = true;
        setTimeSeriesLoading(true);
        try {
          const data = await generatePolicyTimeSeriesData(location, scenario, emfacVersion);
          // Cache the result in old cache for backward compatibility
          timeSeriesCache.set(cacheKey, data);
          // Limit cache size to prevent memory issues
          if (timeSeriesCache.size > 10) {
            const firstKey = timeSeriesCache.keys().next().value;
            if (firstKey) {
              timeSeriesCache.delete(firstKey);
            }
          }
          setTimeSeriesData(data);
        } catch (error) {
          console.error('Error generating time series data:', error);
        } finally {
          setTimeSeriesLoading(false);
          generatingRef.current = false;
        }
      }
    };

    generateTimeSeries();
    
    return () => {
      generatingRef.current = false;
    };
  }, [location, scenarioKey, emfacVersion, scenario]);

  // Compute per-year VMT series so we can show LDV intensity (kg/mile) in tooltips.
  // This helps distinguish "emissions changed because VMT changed" vs "intensity changed".
  const vmtByYear = useMemo(() => {
    // Baseline might be computed asynchronously; re-check once timeSeriesData is available.
    // Using `timeSeriesData` as a dependency ensures we recompute after the baseline cache warms.
    const baseline = getCachedBaseline(location);
    if (!baseline) return null;

    // Build a simple interpolator for EMFAC forecasting ratios.
    const rows = Array.isArray((baseline as any).emfacData) ? ((baseline as any).emfacData as any[]) : [];
    const byYear = new Map<number, any>();
    rows.forEach((r) => {
      const y = Number(r?.year);
      if (!Number.isFinite(y)) return;
      byYear.set(y, r);
    });
    const years = Array.from(byYear.keys()).sort((a, b) => a - b);
    const getRatio = (y: number): number => {
      const exact = byYear.get(y)?.forecasting_ratio;
      if (typeof exact === 'number' && Number.isFinite(exact) && exact > 0) return exact;
      if (years.length === 0) return 1;
      // Find bracketing years; linear interpolate.
      let lo: number | undefined;
      let hi: number | undefined;
      for (let i = 0; i < years.length; i++) {
        const yy = years[i];
        if (yy < y) lo = yy;
        if (yy > y) { hi = yy; break; }
      }
      const loVal = lo !== undefined ? byYear.get(lo)?.forecasting_ratio : undefined;
      const hiVal = hi !== undefined ? byYear.get(hi)?.forecasting_ratio : undefined;
      if (lo !== undefined && hi !== undefined && lo !== hi &&
          typeof loVal === 'number' && Number.isFinite(loVal) &&
          typeof hiVal === 'number' && Number.isFinite(hiVal)) {
        const t = (y - lo) / (hi - lo);
        return loVal + (hiVal - loVal) * t;
      }
      // Extrapolate with last known value.
      const last = years[years.length - 1];
      const lastVal = byYear.get(last)?.forecasting_ratio;
      return (typeof lastVal === 'number' && Number.isFinite(lastVal) && lastVal > 0) ? lastVal : 1;
    };

    const baselineVmt2023 = Number((baseline as any).baselineVMT) || 0;
    const out = new Map<number, { baselineVmt: number; policyVmt: number }>();
    const totalSteps = 2045 - 2023;

    for (let y = 2005; y <= 2045; y++) {
      const ratio = getRatio(y);
      const baseVmt = baselineVmt2023 > 0 ? baselineVmt2023 * ratio : 0;
      const yearsElapsed = y - 2023;
      const annualVmtr = (Number(scenario.vmtReductionPercent) || 0) / totalSteps;
      const vmtReductionPct = yearsElapsed <= 0 ? 0 : Math.min(Number(scenario.vmtReductionPercent) || 0, annualVmtr * yearsElapsed);
      const policyVmt = baseVmt * (1 - vmtReductionPct / 100);
      out.set(y, { baselineVmt: baseVmt, policyVmt });
    }

    return out;
  }, [scenario.vmtReductionPercent, location]);

  // Cache for forecast data
  const forecastCache = useRef(new Map<string, {
    years: number[];
    bauTotals: number[];
    withPoliciesTotals: number[];
    vmtWedge: number[];
    evWedge: number[];
  }>());

  // Build forecast data whenever chart type or scenario changes
  useEffect(() => {
    if (chartType !== 'forecast' || !location || !results) {
      setForecastData(null);
      return;
    }

    const buildForecast = async () => {
      try {
        // Create cache key for forecast
        const forecastCacheKey = `${scenarioKey}:forecast:${results.baseline.currentEVPercent}`;
        
        // Check cache first
        if (forecastCache.current.has(forecastCacheKey)) {
          const cached = forecastCache.current.get(forecastCacheKey);
          if (cached) {
            setForecastData(cached);
            return;
          }
        }

        // Helper: zero-out all non-transport levers so BAU and isolated cases are unaffected by unrelated policies
        const zeroedPolicies = {
          heatPumpAdoptionPercent: 0,
          solarCapacityMultiplier: 1,
          hdvElectrificationPercent: 0,
          vmtReductionComponents: (scenario as any).vmtReductionComponents || {
            transitOrientedDevelopment: 0,
            activeTransportation: 0,
            transitImprovements: 0,
          },
          parkingPricing: (scenario as any).parkingPricing || { priceIncreasePercent: 0, vmtAffectedPercent: 0, commuteByCarPercent: 0, basePricePerHour: 0 },
          micromobility: (scenario as any).micromobility || { scalePercent: 0, tripsPerResident: 0, percentBike: 0, percentScooter: 0, milesPerBikeTrip: 0, milesPerScooterTrip: 0, percentBikeTripsDisplaceCars: 0, percentScooterTripsDisplaceCars: 0, kwhPerMileBike: 0, kwhPerMileScooter: 0, yearImplemented: 2025 },
        } as Partial<PolicyScenario>;

        // BAU (no VMT reduction, no added EV beyond current, all other policy levers neutral)
        const bauScenario: PolicyScenario = {
          ...scenario,
          ...zeroedPolicies,
          vmtReductionPercent: 0,
          evAdoptionPercent: results?.baseline.currentEVPercent || 0,
        } as PolicyScenario;
        // Get cached baseline for synchronous transformations
        const baseline = getCachedBaseline(location);
        
        // Helper function to get time series data (synchronous if baseline cached, async otherwise)
        const getTimeSeriesData = async (scenario: PolicyScenario, cacheKey: string) => {
          // Check old cache first
          const cached = timeSeriesCache.get(cacheKey);
          if (cached) return cached;
          
          // If we have a baseline, apply transformations synchronously
          if (baseline) {
            const transformedProjections = applyPolicyTransformations(baseline, scenario);
            const historicalUpTo2022 = baseline.historicalData.filter((d: TimeSeriesEmissionData) => d.year < 2023);
            const baselineData = [...historicalUpTo2022, ...baseline.baselineProjections];
            const projectedWithPolicies = [...historicalUpTo2022, ...transformedProjections];
            const result = {
              baselineData,
              projectedData: projectedWithPolicies,
              location
            };
            timeSeriesCache.set(cacheKey, result);
            return result;
          }
          
          // No baseline - calculate asynchronously
          const result = await generatePolicyTimeSeriesData(location, scenario);
          timeSeriesCache.set(cacheKey, result);
          return result;
        };

        const bauKey = createScenarioKey(location, bauScenario, emfacVersion);
        const bau = await getTimeSeriesData(bauScenario, bauKey);

        // VMT only
        const vmtOnlyScenario: PolicyScenario = {
          ...scenario,
          ...zeroedPolicies,
          vmtReductionPercent: scenario.vmtReductionPercent || 0,
          evAdoptionPercent: results?.baseline.currentEVPercent || 0,
        } as PolicyScenario;
        const vmtOnlyKey = createScenarioKey(location, vmtOnlyScenario);
        const vmtOnly = await getTimeSeriesData(vmtOnlyScenario, vmtOnlyKey);

        // With both (current scenario)
        const bothScenario: PolicyScenario = {
          ...scenario,
          heatPumpAdoptionPercent: scenario.heatPumpAdoptionPercent || 0,
          solarCapacityMultiplier: scenario.solarCapacityMultiplier || 1,
          hdvElectrificationPercent: scenario.hdvElectrificationPercent || 0,
        } as PolicyScenario;
        const bothKey = createScenarioKey(location, bothScenario);
        const both = await getTimeSeriesData(bothScenario, bothKey);

        const yrs = bau.projectedData.map(r => r.year);
        const sumCore = (row: TimeSeriesEmissionData) => (row.lightDutyEmissions + row.heavyDutyEmissions + row.naturalGasEmissions + row.electricityEmissions);

        // Compute "Other" baseline (everything not LDV/HDV/NG/Electricity) using current results baseline
        const coreBaseline =
          (results?.baseline.lightDutyGHG || 0) +
          (results?.baseline.heavyDutyGHG || 0) +
          (results?.baseline.naturalGasGHG || 0) +
          (timeSeriesData?.baselineData?.[timeSeriesData.baselineData.length - 1]?.electricityEmissions || 0);
        const otherBaseline = Math.max(0, (results?.baseline.totalGHG || 0) - coreBaseline);

        // Fetch population series and project to requested years (fallback: flat)
        let popByYear: Record<number, number> = {};
        try {
          const { data: popRows } = await supabaseClient
            .from('population')
            .select('year, population')
            .eq('location_name', location)
            .order('year', { ascending: true });
          if (popRows && popRows.length > 0) {
            popRows.forEach((r: any) => { popByYear[r.year] = r.population; });
            // Simple linear extrapolation to 2045 if needed
            const yearsAvail = Object.keys(popByYear).map(y => parseInt(y)).sort((a,b)=>a-b);
            const lastYear = yearsAvail[yearsAvail.length - 1];
            const prevYear = yearsAvail[yearsAvail.length - 2] || lastYear - 1;
            const growth = lastYear > prevYear ? (popByYear[lastYear] - popByYear[prevYear]) / (lastYear - prevYear) : 0;
            const neededYears = yrs.filter(y => !(y in popByYear));
            neededYears.forEach(y => { popByYear[y] = popByYear[lastYear] + growth * (y - lastYear); });
          }
        } catch (e) {
          console.warn('Population lookup failed; using flat other emissions', e);
        }
        const basePop = popByYear[2023] || popByYear[Object.keys(popByYear).map(n=>parseInt(n)).sort((a,b)=>b-a)[0]] || 1;
        const otherByYear = yrs.map(y => Math.round(otherBaseline * (popByYear[y] ? popByYear[y] / basePop : 1)));

        const sumTotals = (rows: TimeSeriesEmissionData[]) => rows.map((r, i) => Math.round(sumCore(r) + (otherByYear[i] || 0)));

        const bauTotals = sumTotals([...bau.baselineData, ...bau.projectedData].slice(bau.baselineData.length));
        const vmtOnlyTotals = sumTotals([...vmtOnly.baselineData, ...vmtOnly.projectedData].slice(vmtOnly.baselineData.length));
        const bothTotals = sumTotals([...both.baselineData, ...both.projectedData].slice(both.baselineData.length));

        // Construct wedges for forecast horizon
        const vmtWedge = bauTotals.map((v, i) => Math.max(0, v - vmtOnlyTotals[i]));
        const evWedge = vmtOnlyTotals.map((v, i) => Math.max(0, v - bothTotals[i]));

        const forecastResult = { years: yrs, bauTotals, withPoliciesTotals: bothTotals, vmtWedge, evWedge };
        
        // Cache the result
        forecastCache.current.set(forecastCacheKey, forecastResult);
        // Limit cache size
        if (forecastCache.current.size > 10) {
          const firstKey = forecastCache.current.keys().next().value;
          if (firstKey) {
            forecastCache.current.delete(firstKey);
          }
        }
        
        setForecastData(forecastResult);
      } catch (e) {
        console.warn('Failed to build forecast data', e);
        setForecastData(null);
      }
    };
    buildForecast();
  }, [chartType, location, scenarioKey, results?.baseline.currentEVPercent, emfacVersion, results, scenario, timeSeriesData?.baselineData]);

  // Memoize chart data preparation to avoid recalculation on every render
  // Must be called before any early returns (React Hooks rule)
  const chartData = useMemo(() => {
    if (!timeSeriesData) return null;

    // Combine baseline and projected data to get all years
    const allYears = [...timeSeriesData.baselineData.map(d => d.year), ...timeSeriesData.projectedData.map(d => d.year)];
    const uniqueYears = Array.from(new Set(allYears)).sort((a, b) => a - b);
    
    // Create maps for easy lookup
    const baselineMap = new Map(timeSeriesData.baselineData.map(d => [d.year, d]));
    const projectedMap = new Map(timeSeriesData.projectedData.map(d => [d.year, d]));
    
    // Line chart shows key categories with baseline vs with-policies.
    const baselineData = {
      lightDuty: uniqueYears.map((year) => baselineMap.get(year)?.lightDutyEmissions ?? null),
      naturalGas: uniqueYears.map((year) => baselineMap.get(year)?.naturalGasEmissions ?? null),
      electricity: uniqueYears.map((year) => baselineMap.get(year)?.electricityEmissions ?? null),
      waste: uniqueYears.map((year) => baselineMap.get(year)?.wasteEmissions ?? null),
    };

    // Show "With Policies" series starting at the baseline year (2023).
    // 2023 itself is unchanged by policies, but including it avoids a visual jump
    // and makes the dotted series clearly start from the baseline.
    const projectedStartYear = 2023;
    const projectedData = {
      lightDuty: uniqueYears.map((year) =>
        year >= projectedStartYear ? (projectedMap.get(year)?.lightDutyEmissions ?? null) : null
      ),
      naturalGas: uniqueYears.map((year) =>
        year >= projectedStartYear ? (projectedMap.get(year)?.naturalGasEmissions ?? null) : null
      ),
      electricity: uniqueYears.map((year) =>
        year >= projectedStartYear ? (projectedMap.get(year)?.electricityEmissions ?? null) : null
      ),
      waste: uniqueYears.map((year) =>
        year >= projectedStartYear ? (projectedMap.get(year)?.wasteEmissions ?? null) : null
      ),
    };

    return {
    labels: uniqueYears.map((y) => y.toString()),
    datasets: [
      {
        label: 'LDV (Baseline)',
        data: baselineData.lightDuty,
        backgroundColor: 'rgba(59, 130, 246, 0.3)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 4,
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'LDV',
        data: projectedData.lightDuty,
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 5,
        borderDash: [5, 5],
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Natural Gas (Baseline)',
        data: baselineData.naturalGas,
        backgroundColor: 'rgba(16, 185, 129, 0.3)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 4,
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Natural Gas',
        data: projectedData.naturalGas,
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 5,
        borderDash: [5, 5],
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Electricity (Baseline)',
        data: baselineData.electricity,
        backgroundColor: 'rgba(245, 158, 11, 0.3)',
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 4,
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Electricity',
        data: projectedData.electricity,
        backgroundColor: 'rgba(245, 158, 11, 0.8)',
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 5,
        borderDash: [5, 5],
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Waste (Baseline)',
        data: baselineData.waste,
        backgroundColor: 'rgba(236, 72, 153, 0.3)',
        borderColor: 'rgba(236, 72, 153, 1)',
        borderWidth: 4,
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Waste',
        data: projectedData.waste,
        backgroundColor: 'rgba(236, 72, 153, 0.8)',
        borderColor: 'rgba(236, 72, 153, 1)',
        borderWidth: 5,
        borderDash: [5, 5],
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
    ],
  };
  }, [timeSeriesData]);

  // Feed PolicyImpactTool metric cards from the same totals used by the stacked chart.
  const computedTotals = useMemo(() => {
    if (!timeSeriesData) return null;
    const baselineYear = timeSeriesData.baselineData.some((d) => d.year === 2023)
      ? 2023
      : timeSeriesData.baselineData[timeSeriesData.baselineData.length - 1]?.year;
    const projectedYear = timeSeriesData.projectedData.some((d) => d.year === 2045)
      ? 2045
      : timeSeriesData.projectedData[timeSeriesData.projectedData.length - 1]?.year;
    if (!baselineYear || !projectedYear) return null;

    const baselineRow = timeSeriesData.baselineData.find((d) => d.year === baselineYear);
    const projectedRow = timeSeriesData.projectedData.find((d) => d.year === projectedYear);
    if (!baselineRow || !projectedRow) return null;

    const baselineTotal = baselineRow.totalEmissions || 0;
    const projectedWithPoliciesTotal = projectedRow.totalEmissions || 0;
    const totalReduction = baselineTotal - projectedWithPoliciesTotal;
    const percentReduction = baselineTotal > 0 ? (totalReduction / baselineTotal) * 100 : 0;

    return {
      baselineYear,
      baselineTotal,
      projectedYear,
      projectedWithPoliciesTotal,
      totalReduction,
      percentReduction,
    };
  }, [timeSeriesData]);

  useEffect(() => {
    if (onTotalsChange && computedTotals) onTotalsChange(computedTotals);
  }, [onTotalsChange, computedTotals]);

  // Line-chart y-axis max (exclude the vertical separator's synthetic spike)
  // Must be declared before any early returns (React Hooks rule)
  const lineChartMaxValue = useMemo(() => {
    if (!timeSeriesData) return undefined;
    // Line chart plots key sector series, so compute max across those values.
    const allValues = [
      ...timeSeriesData.baselineData.flatMap((d) => [
        d.lightDutyEmissions,
        d.naturalGasEmissions,
        d.electricityEmissions,
        d.wasteEmissions,
      ]),
      ...timeSeriesData.projectedData.flatMap((d) => [
        d.lightDutyEmissions,
        d.naturalGasEmissions,
        d.electricityEmissions,
        d.wasteEmissions,
      ]),
    ].filter((v) => typeof v === 'number' && Number.isFinite(v)) as number[];

    return allValues.length ? Math.max(...allValues) : 0;
  }, [timeSeriesData]);

  const lineChartYAxis = useMemo(() => {
    if (lineChartMaxValue === undefined) return { max: undefined as number | undefined, stepSize: undefined as number | undefined };
    const { max, stepSize } = getNiceAxisMaxAndStep(lineChartMaxValue, 6);
    // Reduce headroom - use a smaller percentage instead of a full step
    return { max: max + (stepSize * 0.1), stepSize };
  }, [lineChartMaxValue]);

  // Stacked area chart data - only "With Policies" for LDV, Natural Gas, and Electricity
  const stackedChartData = useMemo(() => {
    if (!timeSeriesData) return null;

    // Combine baseline and projected data to get all years
    const allYears = [...timeSeriesData.baselineData.map(d => d.year), ...timeSeriesData.projectedData.map(d => d.year)];
    const uniqueYears = Array.from(new Set(allYears)).sort((a, b) => a - b);
    
    // Create maps for easy lookup
    const baselineMap = new Map(timeSeriesData.baselineData.map(d => [d.year, d]));
    const projectedMap = new Map(timeSeriesData.projectedData.map(d => [d.year, d]));
    
    // Get "With Policies" data for the three categories
    const projectedData = {
      // Use nullish coalescing so real zeros remain zeros (important for clean-electricity-year ramps).
      lightDuty: uniqueYears.map(year => projectedMap.get(year)?.lightDutyEmissions ?? null),
      naturalGas: uniqueYears.map(year => projectedMap.get(year)?.naturalGasEmissions ?? null),
      electricity: uniqueYears.map(year => projectedMap.get(year)?.electricityEmissions ?? null),
      waste: uniqueYears.map(year => projectedMap.get(year)?.wasteEmissions ?? null),
      heavyDuty: uniqueYears.map(year => projectedMap.get(year)?.heavyDutyEmissions ?? null),
      offroad: uniqueYears.map(year => projectedMap.get(year)?.offroadEmissions ?? null),
    };

    // Calculate target line value based on baseline year
    // NOTE: baselineData may not contain the selected baseline year (e.g. 2005),
    // so fall back to projectedMap. Also, this stacked chart only shows LDV+NG+Electricity,
    // so compute target on the same scope to keep it visible on the same y-scale.
    const baselineYearData = baselineMap.get(targetBaselineYear) ?? projectedMap.get(targetBaselineYear);
    const baselineYearTotal = baselineYearData
      ? baselineYearData.lightDutyEmissions +
        baselineYearData.naturalGasEmissions +
        baselineYearData.electricityEmissions +
        (baselineYearData as any).wasteEmissions +
        baselineYearData.heavyDutyEmissions +
        (baselineYearData as any).offroadEmissions
      : 0;
    const targetValue = baselineYearTotal * (1 - targetReductionPercent / 100);
    
    // Create target line data (same value for all years)
    const targetLineData = uniqueYears.map(() => targetValue);

    // Calculate sum of stacked areas (With Policies) for each year
    const stackedAreasTotal = uniqueYears.map((year, index) => {
      const lightDuty = projectedData.lightDuty[index];
      const heavyDuty = projectedData.heavyDuty[index];
      const naturalGas = projectedData.naturalGas[index];
      const electricity = projectedData.electricity[index];
      const waste = projectedData.waste[index];
      const offroad = projectedData.offroad[index];
      
      const sum = (lightDuty || 0) + (heavyDuty || 0) + (naturalGas || 0) + 
                  (electricity || 0) + (waste || 0) + (offroad || 0);
      return sum > 0 ? sum : null;
    });

    // Baseline total line: sum of all emissions before policies
    // For historical years: use baselineData
    // For projected years: baseline projections should be in baselineData, but if missing, 
    // we need to calculate from baseline projections (not from "with policies" data)
    const baselineTotalData = uniqueYears.map((year, index) => {
      const baselineRow = baselineMap.get(year);
      if (baselineRow) {
        // We have baseline data for this year - sum all sources
        const baselineSum = (baselineRow.lightDutyEmissions || 0) +
               (baselineRow.heavyDutyEmissions || 0) +
               (baselineRow.naturalGasEmissions || 0) +
               (baselineRow.electricityEmissions || 0) +
               (baselineRow.wasteEmissions || 0) +
               (baselineRow.offroadEmissions || 0);
        return baselineSum;
      }
      // For projected years without baseline data, we should not fall back to stacked areas
      // Instead, return null so the line doesn't extend incorrectly
      // The baseline projections should be in baselineData, so this shouldn't happen
      console.warn(`⚠️ No baseline data found for year ${year}, baseline total line may be incorrect`);
      return null;
    });

    // Calculate Reductions wedge: difference between baseline total and stacked areas total
    // Show from 2023 onwards (reductions start from baseline year)
    // The value represents how much to stack on top of the stacked areas to reach the baseline
    const reductionsData = uniqueYears.map((year, index) => {
      // Use 0 instead of null for years before 2023 to prevent Chart.js from drawing lines
      if (year < 2023) return 0; // No reductions before 2023 - use 0 to prevent line extension
      const baseline = baselineTotalData[index];
      const stacked = stackedAreasTotal[index];
      if (baseline === null || stacked === null) return 0;
      
      // Debug: Log if baseline is less than stacked (should never happen)
      if (baseline < stacked && year >= 2023) {
        console.warn(`⚠️ Year ${year}: Baseline (${baseline.toFixed(0)}) is less than stacked areas (${stacked.toFixed(0)}). This indicates a calculation error.`);
        const baselineRow = baselineMap.get(year);
        const projectedRow = projectedMap.get(year);
        if (baselineRow) {
          console.warn(`   Baseline breakdown: LDV=${baselineRow.lightDutyEmissions?.toFixed(0)}, HDV=${baselineRow.heavyDutyEmissions?.toFixed(0)}, NG=${baselineRow.naturalGasEmissions?.toFixed(0)}, Elec=${baselineRow.electricityEmissions?.toFixed(0)}, Waste=${baselineRow.wasteEmissions?.toFixed(0)}, Offroad=${baselineRow.offroadEmissions?.toFixed(0)}`);
        }
        if (projectedRow) {
          console.warn(`   Projected breakdown: LDV=${projectedRow.lightDutyEmissions?.toFixed(0)}, HDV=${projectedRow.heavyDutyEmissions?.toFixed(0)}, NG=${projectedRow.naturalGasEmissions?.toFixed(0)}, Elec=${projectedRow.electricityEmissions?.toFixed(0)}, Waste=${projectedRow.wasteEmissions?.toFixed(0)}, Offroad=${projectedRow.offroadEmissions?.toFixed(0)}`);
        }
      }
      
      // Exact reductions wedge = Baseline total - With Policies total (clamped at 0 so we don't render negative wedges).
      return Math.max(0, baseline - stacked);
    });

    return {
      labels: uniqueYears.map((y) => y.toString()),
      datasets: [
        // Stacked areas in order from bottom to top (for legend alignment)
        {
          label: 'LDV',
          data: projectedData.lightDuty,
          backgroundColor: 'rgba(59, 130, 246, 0.7)',
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 0, // Bottom of stack (match GHG Inventory ordering)
        },
        {
          label: 'HDV',
          data: projectedData.heavyDuty,
          backgroundColor: 'rgba(96, 165, 250, 0.7)',
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 1,
        },
        {
          label: 'Natural Gas',
          data: projectedData.naturalGas,
          backgroundColor: 'rgba(16, 185, 129, 0.7)',
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 2,
        },
        {
          label: 'Electricity',
          data: projectedData.electricity,
          backgroundColor: 'rgba(245, 158, 11, 0.7)',
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 3,
        },
        {
          label: 'Waste',
          data: projectedData.waste,
          backgroundColor: 'rgba(236, 72, 153, 0.7)',
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 4,
        },
        {
          label: 'Offroad',
          data: projectedData.offroad,
          backgroundColor: 'rgba(255, 210, 0, 0.65)',
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 5,
        },
        {
          label: 'Reductions',
          data: reductionsData,
          backgroundColor: 'rgba(107, 114, 128, 0.7)', // Gray fill - solid color for independent wedge
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main', // Stack on top of other areas - this creates the wedge between stacked areas and baseline
          order: 6, // After all other stacked areas
          spanGaps: false, // Don't connect through zero values
        },
        {
          label: 'Total (Baseline)',
          data: baselineTotalData,
          backgroundColor: 'rgba(0, 0, 0, 0)',
          borderColor: 'rgba(107, 114, 128, 1)', // Gray color instead of black
          borderWidth: 2,
          fill: false,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 6,
          order: 10, // Draw on top of stacked areas and reductions wedge
          spanGaps: false,
        },
        {
          label: 'Target',
          data: targetLineData,
          backgroundColor: 'transparent',
          borderColor: 'transparent', // Completely invisible - drawn manually in afterDraw plugin
          borderWidth: 0.1, // Minimal width so Chart.js processes the dataset
          fill: false,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          borderDash: [],
          order: 99, // Keep legend entry; line drawn manually
          // No stack property - this line won't be stacked
          spanGaps: false,
        },
      ],
    };
  }, [timeSeriesData, targetReductionPercent, targetBaselineYear]);

  // Calculate target value for stacked chart
  const stackedChartTargetValue = useMemo(() => {
    if (!timeSeriesData) return null;
    const baselineMap = new Map(timeSeriesData.baselineData.map(d => [d.year, d]));
    const projectedMap = new Map(timeSeriesData.projectedData.map(d => [d.year, d]));
    const baselineYearData = baselineMap.get(targetBaselineYear) ?? projectedMap.get(targetBaselineYear);
    const baselineYearTotal = baselineYearData
      ? baselineYearData.lightDutyEmissions +
        baselineYearData.naturalGasEmissions +
        baselineYearData.electricityEmissions +
        (baselineYearData as any).wasteEmissions +
        baselineYearData.heavyDutyEmissions +
        (baselineYearData as any).offroadEmissions
      : 0;
    return baselineYearTotal * (1 - targetReductionPercent / 100);
  }, [timeSeriesData, targetBaselineYear, targetReductionPercent]);

  // Calculate max value for stacked chart y-axis (sum of all components for each year)
  const stackedChartMaxValue = useMemo(() => {
    if (!timeSeriesData) return undefined;
    const projectedMap = new Map(timeSeriesData.projectedData.map(d => [d.year, d]));
    const baselineMap = new Map(timeSeriesData.baselineData.map(d => [d.year, d]));
    
    // Get all years
    const allYears = [...timeSeriesData.baselineData.map(d => d.year), ...timeSeriesData.projectedData.map(d => d.year)];
    const uniqueYears = Array.from(new Set(allYears)).sort((a, b) => a - b);
    
    // Calculate total for each year (sum of all components)
    const totals = uniqueYears.map(year => {
      // Use projected data (With Policies) for the sum
      const projectedRow = projectedMap.get(year);
      if (projectedRow) {
        return (projectedRow.lightDutyEmissions || 0) +
               (projectedRow.heavyDutyEmissions || 0) +
               (projectedRow.naturalGasEmissions || 0) +
               (projectedRow.electricityEmissions || 0) +
               (projectedRow.wasteEmissions || 0) +
               (projectedRow.offroadEmissions || 0);
      }
      // Fallback to baseline if projected not available
      const baselineRow = baselineMap.get(year);
      if (baselineRow) {
        return (baselineRow.lightDutyEmissions || 0) +
               (baselineRow.heavyDutyEmissions || 0) +
               (baselineRow.naturalGasEmissions || 0) +
               (baselineRow.electricityEmissions || 0) +
               (baselineRow.wasteEmissions || 0) +
               (baselineRow.offroadEmissions || 0);
      }
      return 0;
    }).filter(v => typeof v === 'number' && Number.isFinite(v) && v > 0) as number[];
    
    return totals.length ? Math.max(...totals) : undefined;
  }, [timeSeriesData]);

  // Calculate y-axis max for stacked chart with reduced headroom
  // Also ensure target line value is included in the range
  const stackedChartYAxis = useMemo(() => {
    if (stackedChartMaxValue === undefined) return { max: undefined as number | undefined, stepSize: undefined as number | undefined };
    
    // Get target value to ensure it's included in y-axis range
    const targetValue = stackedChartTargetValue;
    const maxValue = targetValue !== null && targetValue > stackedChartMaxValue 
      ? targetValue 
      : stackedChartMaxValue;
    
    const { max, stepSize } = getNiceAxisMaxAndStep(maxValue, 6);
    // Reduce headroom - use a smaller percentage instead of a full step
    return { max: max + (stepSize * 0.1), stepSize };
  }, [stackedChartMaxValue, stackedChartTargetValue]);

  // Stacked chart options
  const stackedChartOptions = useMemo(() => {
    return {
      type: 'line' as const,
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 10, // Reduced since legend is on the right
          bottom: 16, // Reduced to minimize white space below chart
          left: 0, // Remove left padding to move chart left
          right: 50, // Further reduced to make chart even wider
        },
      },
      interaction: {
        intersect: false,
        mode: 'index' as const,
      },
      plugins: {
        // Opt-in for the global verticalSeparator/targetLine plugin (Policy Tool only)
        verticalSeparator: { enabled: true },
        legend: {
          position: 'right' as const,
          align: 'center' as const, // Center legend vertically in available space
          labels: {
            usePointStyle: false,
            padding: 6, // Reduced padding between legend items
            boxWidth: 12,
            boxHeight: 12,
            font: {
              size: 13, // Increased legend font size
            },
            generateLabels: function(chart: any) {
              const original = Chart.defaults.plugins.legend.labels.generateLabels;
              const labels = original.call(this, chart);

              // Reorder labels to match chart order from bottom to top
              // Chart visual order (bottom to top): Target (line), LDV (0), HDV (1), Natural Gas (2), Electricity (3), Waste (4), Offroad (5), Reductions (6), Total Baseline (line)
              // Legend order (reading bottom to top): Target, LDV, HDV, Natural Gas, Electricity, Waste, Offroad, Reductions, Total Baseline
              // With sort orderA - orderB: lower numbers appear first in array (top of legend), higher numbers appear last (bottom of legend)
              // So we need: Target (highest order, bottom), then stack items in reverse (LDV highest, Reductions lowest), then Total Baseline (lowest order, top)
              const orderMap = new Map<string, number>();
              chart.data.datasets.forEach((dataset: any) => {
                if (dataset.stack === 'main') {
                  // Reverse the order: LDV (0) → 6, HDV (1) → 5, Natural Gas (2) → 4, etc.
                  // This makes LDV appear right after Target when reading bottom-up
                  const originalOrder = dataset.order ?? 999;
                  orderMap.set(dataset.label, 6 - originalOrder);
                } else if (dataset.label === 'Total (Baseline)') {
                  orderMap.set(dataset.label, -1); // Lowest order so it appears at TOP of legend
                } else if (dataset.label === 'Target') {
                  orderMap.set(dataset.label, 10); // Highest order so it appears at BOTTOM of legend
                } else {
                  orderMap.set(dataset.label, 999);
                }
              });

              // Sort: Total Baseline (-1) at top, then Reductions (0), Offroad (1), Waste (2), Electricity (3), Natural Gas (4), HDV (5), LDV (6), Target (10) at bottom
              // When reading legend from bottom to top: Target, LDV, HDV, Natural Gas, Electricity, Waste, Offroad, Reductions, Total Baseline ✓
              labels.sort((a: any, b: any) => {
                const orderA = orderMap.get(a.text) ?? 999;
                const orderB = orderMap.get(b.text) ?? 999;
                return orderA - orderB;
              });

              labels.forEach((label: any) => {
                const dataset = chart.data.datasets.find((d: any) => d.label === label.text);
                if (dataset?.label && dataset.label === 'Target') {
                  label.strokeStyle = 'rgba(0, 0, 0, 1)';
                  label.lineWidth = 1.5; // Reduced thickness for target line
                  label.fillStyle = 'rgba(0, 0, 0, 0)';
                  label.lineDash = [12, 6];
                }
              });

              return labels;
            },
          },
        },
        title: {
          display: false,
        },
        tooltip: {
          enabled: true,
          mode: 'index' as const,
          intersect: false,
          itemSort: (a: any, b: any) => {
            // Use the same order mapping as the legend
            const getOrder = (label: string) => {
              if (label === 'Total (Baseline)') return -1;
              if (label === 'Target') return 10;
              // For stacked items, reverse the order to match legend
              const dataset = (stackedChartData as any)?.datasets?.find((d: any) => d.label === label);
              if (dataset?.stack === 'main' && typeof dataset.order === 'number') {
                return 6 - dataset.order; // Reverse: LDV (0) → 6, HDV (1) → 5, etc.
              }
              return 999;
            };
            const orderA = getOrder(a.dataset.label || '');
            const orderB = getOrder(b.dataset.label || '');
            return orderA - orderB; // Same sort as legend
          },
          callbacks: {
            label: (context: any) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              return `${label}: ${value.toLocaleString()} MT CO₂e`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          type: 'category' as const,
          title: {
            display: false, // Removed "Year" label to save space
          },
          offset: false, // Remove offset to eliminate extra spacing
          bounds: 'data' as const, // Use exact data bounds to eliminate extra space
          min: undefined, // Let Chart.js determine from data
          max: undefined, // Let Chart.js determine from data
          grid: {
            display: true,
            offset: false, // Remove grid offset to align with data points
            color: (context: any) => {
              const label = context.tick?.label;
              if (label === '2023') {
                return 'rgba(107, 114, 128, 0.6)';
              }
              return 'rgba(0, 0, 0, 0.06)';
            },
            lineWidth: (context: any) => {
              const label = context.tick?.label;
              return label === '2023' ? 2 : 1;
            },
          },
          ticks: {
            display: true,
            autoSkip: false,
            maxTicksLimit: 50,
            callback: function(value: any, index: number, ticks: any[]) {
              const labels = (stackedChartData as any)?.labels || [];
              if (labels.length === 0) return '';
              const label = labels[index];
              const yearNum = parseInt(String(label), 10);
              if (Number.isNaN(yearNum)) return '';

              // Exclude 2023 and 2024
              if (yearNum === 2023 || yearNum === 2024) {
                return '';
              }

              // Always show first (2005) and last (2045) year
              if (index === 0 || index === labels.length - 1) {
                return String(yearNum);
              }

              // Show every 5 years
              if (yearNum % 5 === 0) {
                return String(yearNum);
              }
              return '';
            },
            maxRotation: 0,
            minRotation: 0,
            font: {
              size: 13, // Increased x-axis font size
            },
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          display: true,
          position: 'left' as const,
          // Set max based on actual data with reduced headroom
          max: stackedChartYAxis.max,
          title: {
            display: true,
            text: 'metric tons CO₂e',
            font: {
              size: 14, // Increased y-axis title font size
              weight: 'normal' as const,
              color: '#374151',
            },
            padding: {
              top: 0,
              bottom: 10,
              left: 0,
              right: 0,
            },
            color: '#374151',
          },
          ticks: {
            display: true,
            stepSize: stackedChartYAxis.stepSize,
            callback: function(value: any) {
              const numValue = typeof value === 'number' ? value : Number(value);
              if (Number.isNaN(numValue)) return '';
              return numValue.toLocaleString();
            },
            font: {
              size: 13, // Increased y-axis ticks font size
            },
            padding: 8,
            autoSkip: false,
            maxTicksLimit: 10,
            color: '#374151',
          },
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.1)',
            drawBorder: true,
            drawOnChartArea: true,
          },
        },
      },
    };
  }, [stackedChartData, stackedChartYAxis]);

  // Build forecast chart data/options when available
  // Must be called before any early returns (React Hooks rule)
  const forecastChartData = useMemo(() => {
    if (!forecastData) return null;
    
    return (() => {
    const bau = forecastData.bauTotals;
    const afterVmt = forecastData.bauTotals.map((v, i) => v - forecastData.vmtWedge[i]);
    const afterEv = afterVmt.map((v, i) => v - forecastData.evWedge[i]);

    // Get 2023 BAU value for flat line
    const year2023Index = forecastData.years.findIndex(y => y === 2023);
    const bau2023Value = year2023Index >= 0 ? bau[year2023Index] : null;
    
    // Create flat BAU line from 2023 to 2045
    const flatBauData = forecastData.years.map((year, index) => {
      if (year < 2023) return null;
      return bau2023Value;
    });

    return {
      labels: forecastData.years,
      datasets: [
        // BAU line with state policies (top reference) - must be first for VMT wedge fill target
        {
          label: 'BAU with state policies',
          data: bau,
          borderColor: 'rgba(31, 41, 55, 1)',
          backgroundColor: 'rgba(31, 41, 55, 0.1)',
          borderWidth: 4,
          pointRadius: 0,
          fill: false,
          tension: 0,
          order: 3,
        },
        // VMT wedge: area between BAU and After-VMT
        {
          label: 'VMT Reduction',
          data: afterVmt,
          borderColor: 'rgba(59, 130, 246, 0)',
          backgroundColor: 'rgba(59, 130, 246, 0.35)',
          pointRadius: 0,
          fill: {
            target: '-1', // fill to previous dataset (BAU with state policies)
          },
          tension: 0,
          order: 2,
        },
        // EV wedge: area between After-VMT and After-EV (With Policies)
        {
          label: 'Vehicle Electrification',
          data: afterEv,
          borderColor: 'rgba(96, 165, 250, 0)',
          backgroundColor: 'rgba(96, 165, 250, 0.35)',
          pointRadius: 0,
          fill: {
            target: '-1', // fill to previous dataset (After-VMT)
          },
          tension: 0,
          order: 1,
        },
        // With policies line (remaining emissions)
        {
          label: 'Total GHG',
          data: afterEv,
          borderColor: 'rgba(34, 197, 94, 1)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 5,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
          tension: 0,
          order: 4,
        },
        // Flat BAU line from 2023 to 2045 (drawn on top)
        {
          label: 'BAU',
          data: flatBauData,
          borderColor: 'rgba(107, 114, 128, 1)',
          backgroundColor: 'rgba(107, 114, 128, 0.1)',
          borderWidth: 3,
          pointRadius: 0,
          fill: false,
          tension: 0,
          order: 5,
        },
      ]
    };
    })();
  }, [forecastData]);

  // Early returns must come after all hooks
  // Only show loading spinner for time series generation (not for PolicyImpactTool's loading state)
  // This allows instant policy transformations to render immediately
  if (timeSeriesLoading) {
    return (
      <div className="policy-impact-loading">
        <div className="loading-spinner"></div>
        <p>Calculating policy impacts...</p>
      </div>
    );
  }

  // If we have time series data, render the chart even if PolicyImpactTool is still loading
  // (this allows instant policy transformations to show immediately)
  if (!timeSeriesData) {
    return (
      <div className="policy-impact-placeholder">
        <p></p>
      </div>
    );
  }

  // If results are not available yet, show a minimal placeholder
  // (but don't block if timeSeriesData is available, as chart can render with that)
  if (!results) {
    return (
      <div className="policy-impact-placeholder">
        <p>Loading baseline data...</p>
      </div>
    );
  }

  const forecastChartOptions = {
    type: 'line' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20, // Reduced to minimize white space
        bottom: 16, // Reduced to minimize white space
        left: 80,
        right: 40,
      },
    },
    plugins: {
      legend: { position: 'top' as const },
      tooltip: { intersect: false, mode: 'index' as const },
    },
    scales: {
      y: { title: { display: true, text: 'Emissions (Metric Tons CO₂e)' } },
      x: {
        title: { display: true, text: 'Year' },
        // Keep endpoints (e.g., 2005, 2045) fully visible
        offset: false,
      },
    },
  };

  const chartOptions = {
    type: 'line' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20, // Reduced to minimize white space
        bottom: 16, // Reduced to minimize white space
        left: 90,   // room for Y-axis labels and title - increased to ensure visibility
        right: 20,
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      // Opt-in for the global verticalSeparator/targetLine plugin (Policy Tool only)
      verticalSeparator: { enabled: true },
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: false,
          padding: 15,
          boxWidth: 12,
          boxHeight: 12,
          font: {
            size: 11,
          },
          generateLabels: function(chart: any) {
            const original = Chart.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            
            labels.forEach((label: any, index: number) => {
              const dataset = chart.data.datasets[index];
              if (dataset.label && dataset.label.includes('With Policies')) {
                // Add custom rendering for dotted lines in legend
                label.lineDash = dataset.borderDash || [0, 0];
              }
              if (dataset.label && dataset.label.includes('Target:')) {
                // Make the Target legend marker visible (target line is drawn manually)
                label.strokeStyle = 'rgba(0, 0, 0, 1)';
                label.lineWidth = 2.5; // match vertical separator + target line thickness
                label.fillStyle = 'rgba(0, 0, 0, 0)'; // transparent fill
                label.lineDash = [12, 6];
              }
              // Truncate long labels to prevent overflow
              if (label.text && label.text.length > 30) {
                label.text = label.text.substring(0, 27) + '...';
              }
            });
            
            return labels;
          }
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            // Add LDV intensity (kg CO2e/mile) in tooltip so users can see linear intensity
            // even when emissions change due to VMT projections.
            const yearLabel = context?.label;
            const year = typeof yearLabel === 'string' ? parseInt(yearLabel, 10) : Number(yearLabel);
            if (Number.isFinite(year) && (label === 'LDV (Baseline)' || label === 'LDV') && vmtByYear) {
              const vmtRow = vmtByYear.get(year);
              const vmt = label === 'LDV (Baseline)' ? vmtRow?.baselineVmt : vmtRow?.policyVmt;
              if (typeof vmt === 'number' && Number.isFinite(vmt) && vmt > 0) {
                const tonsPerMile = value / vmt;
                const kgPerMile = tonsPerMile * 1000;
                return `${label}: ${value.toLocaleString()} MT CO₂e (${kgPerMile.toFixed(4)} kg CO₂e/mile)`;
              }
            }
            return `${label}: ${value.toLocaleString()} MT CO₂e`;
          },
        },
      },
      // Vertical separator is handled by the global plugin (opt-in via plugins.verticalSeparator.enabled)
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Year',
        },
        // Use offset to ensure first and last data points are fully visible
        offset: true,
        min: undefined, // Let Chart.js determine from data
        max: undefined, // Let Chart.js determine from data
        grid: {
          display: true,
          color: (context: any) => {
            // Highlight 2023 with a darker grid line
            const label = context.tick?.label;
            if (label === '2023') {
              return 'rgba(107, 114, 128, 0.6)';
            }
            return 'rgba(0, 0, 0, 0.06)';
          },
          lineWidth: (context: any) => {
            const label = context.tick?.label;
            return label === '2023' ? 2 : 1;
          },
        },
        ticks: {
          display: true,
          autoSkip: false,
          maxTicksLimit: 50, // Increased to show more years
          // Show labels every 5 years (2005, 2010, ...), and always show first/last
          // Exclude 2023 and 2024 from display
          callback: function(value: any, index: number, ticks: any[]) {
            const labels = (chartData as any)?.labels || [];
            if (labels.length === 0) return '';
            const label = labels[index];
            const yearNum = parseInt(String(label), 10);
            if (Number.isNaN(yearNum)) return '';
            
            // Exclude 2023 and 2024
            if (yearNum === 2023 || yearNum === 2024) {
              return '';
            }
            
            // Always show first and last year
            if (index === 0 || index === labels.length - 1) {
              return String(yearNum);
            }
            
            // Show every 5 years
            if (yearNum % 5 === 0) {
              return String(yearNum);
            }
            return '';
          },
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        display: true,
        position: 'left' as const,
        // Set max based on actual data, excluding vertical separator
        max: lineChartYAxis.max,
        title: {
          display: true,
          text: 'metric tons CO₂e',
          font: {
            size: 12,
            weight: 'normal' as const,
            color: '#374151',
          },
          padding: {
            top: 0,
            bottom: 10,
            left: 0,
            right: 0,
          },
          color: '#374151',
        },
        ticks: {
          display: true,
          showLabelBackdrop: false,
          stepSize: lineChartYAxis.stepSize,
          callback: function(value: any) {
            const numValue = typeof value === 'number' ? value : Number(value);
            if (Number.isNaN(numValue)) return '';
            return Math.round(numValue).toLocaleString();
          },
          font: {
            size: 11,
          },
          padding: 8,
          autoSkip: false,
          maxTicksLimit: 10,
          color: '#374151',
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: true,
          drawOnChartArea: true,
        },
      },
    },
  };

  // Calculate total reduction for percentages
  const totalReduction = results.reductions.totalReduction;

  // Check if we have VMT component breakdown
  const hasVmtBreakdown = results.contributionsByPolicy.vmtReductionByComponent;
  const vmtComponents = results.contributionsByPolicy.vmtReductionByComponent;

  // Create labels with policy values and percentages
  const contributions = hasVmtBreakdown && vmtComponents
    ? [
        {
          name: 'Vehicle Electrification',
          value: Math.round(results.contributionsByPolicy.evAdoption),
          setting: `${scenario.evAdoptionPercent}%`,
        },
        {
          name: 'Energy Efficiency',
          value: Math.round((results.contributionsByPolicy as any).energyEfficiency || 0),
          setting: `${scenario.energyEfficiencyPercent || 0}%`,
        },
        {
          name: 'VMT: Transit-Oriented Dev',
          value: Math.round(vmtComponents.transitOrientedDevelopment),
          setting: `${scenario.vmtReductionComponents.transitOrientedDevelopment.toFixed(1)}%`,
        },
        {
          name: 'VMT: Active Transportation',
          value: Math.round(vmtComponents.activeTransportation),
          setting: `${scenario.vmtReductionComponents.activeTransportation.toFixed(1)}%`,
        },
        {
          name: 'VMT: Transit Improvements',
          value: Math.round(vmtComponents.transitImprovements),
          setting: `${scenario.vmtReductionComponents.transitImprovements.toFixed(1)}%`,
        },
        {
          name: 'Parking Pricing',
          value: Math.round(results.contributionsByPolicy.parkingPricing),
          setting: `${scenario.parkingPricing.priceIncreasePercent}% price increase`,
        },
        {
          name: 'Expand Micromobility',
          value: Math.round(results.contributionsByPolicy.micromobility || 0),
          setting: `${scenario.micromobility?.scalePercent || 0}% scale`,
        },
        {
          name: 'Heat Pumps',
          value: Math.round(results.contributionsByPolicy.heatPumpAdoption),
          setting: `${scenario.heatPumpAdoptionPercent}%`,
        },
        {
          name: 'Solar Capacity',
          value: Math.round(results.contributionsByPolicy.solarCapacity),
          setting: `${scenario.solarCapacityMultiplier}x`,
        },
        {
          name: 'HDV Electrification',
          value: Math.round(results.contributionsByPolicy.hdvElectrification),
          setting: `${scenario.hdvElectrificationPercent}%`,
        },
        {
          name: 'Organics Diversion',
          value: Math.round(results.contributionsByPolicy.organicsDiversion || 0),
          setting: `${(scenario as any).organicsDiversionPercent || 50}%`,
        },
      ]
    : [
        {
          name: 'Vehicle Electrification',
          value: Math.round(results.contributionsByPolicy.evAdoption),
          setting: `${scenario.evAdoptionPercent}%`,
        },
        {
          name: 'Energy Efficiency',
          value: Math.round((results.contributionsByPolicy as any).energyEfficiency || 0),
          setting: `${scenario.energyEfficiencyPercent || 0}%`,
        },
        {
          name: 'VMT Reduction',
          value: Math.round(results.contributionsByPolicy.vmtReduction),
          setting: `${scenario.vmtReductionPercent}%`,
        },
        {
          name: 'Parking Pricing',
          value: Math.round(results.contributionsByPolicy.parkingPricing),
          setting: `${scenario.parkingPricing.priceIncreasePercent}% price increase`,
        },
        {
          name: 'Expand Micromobility',
          value: Math.round(results.contributionsByPolicy.micromobility || 0),
          setting: `${scenario.micromobility?.scalePercent || 0}% scale`,
        },
        {
          name: 'Heat Pumps',
          value: Math.round(results.contributionsByPolicy.heatPumpAdoption),
          setting: `${scenario.heatPumpAdoptionPercent}%`,
        },
        {
          name: 'Solar Capacity',
          value: Math.round(results.contributionsByPolicy.solarCapacity),
          setting: `${scenario.solarCapacityMultiplier}x`,
        },
        {
          name: 'HDV Electrification',
          value: Math.round(results.contributionsByPolicy.hdvElectrification),
          setting: `${scenario.hdvElectrificationPercent}%`,
        },
        {
          name: 'Organics Diversion',
          value: Math.round(results.contributionsByPolicy.organicsDiversion || 0),
          setting: `${(scenario as any).organicsDiversionPercent || 50}%`,
        },
      ];

  // Prepare data for waterfall chart showing contributions
  const truncate = (text: string, max: number = 28) =>
    text.length > max ? `${text.slice(0, max - 1)}…` : text;
  const fmtTons = (v: number) => (v >= 1000 ? `${Math.round(v/100)/10}k` : `${Math.round(v)}`);

  const contributionsData = {
    labels: contributions.map((c) => truncate(`${c.name} (${c.setting}, ${fmtTons(c.value)} t)`)),
    datasets: [
      {
        label: 'GHG Reduction (MT CO₂e)',
        data: contributions.map((c) => c.value),
        backgroundColor: hasVmtBreakdown
          ? [
              'rgba(59, 130, 246, 0.8)', // EV Adoption - Blue
              'rgba(5, 150, 105, 0.8)', // VMT: TOD - Green-600
              'rgba(16, 185, 129, 0.8)', // VMT: Active Transport - Green-500
              'rgba(110, 231, 183, 0.8)', // VMT: Transit - Green-300
              'rgba(34, 197, 94, 0.8)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 0.8)', // Micromobility - Green-700
              'rgba(245, 158, 11, 0.8)', // Heat Pumps - Orange
              'rgba(139, 92, 246, 0.8)', // Solar - Purple
              'rgba(236, 72, 153, 0.8)', // HDV - Pink
            ]
          : [
              'rgba(59, 130, 246, 0.8)',
              'rgba(16, 185, 129, 0.8)',
              'rgba(34, 197, 94, 0.8)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 0.8)', // Micromobility - Green-700
              'rgba(245, 158, 11, 0.8)',
              'rgba(139, 92, 246, 0.8)',
              'rgba(236, 72, 153, 0.8)',
            ],
        borderColor: hasVmtBreakdown
          ? [
              'rgba(59, 130, 246, 1)',
              'rgba(5, 150, 105, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(52, 211, 153, 1)',
              'rgba(110, 231, 183, 1)',
              'rgba(34, 197, 94, 1)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 1)', // Micromobility - Green-700
              'rgba(245, 158, 11, 1)',
              'rgba(139, 92, 246, 1)',
              'rgba(236, 72, 153, 1)',
            ]
          : [
              'rgba(59, 130, 246, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(34, 197, 94, 1)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 1)', // Micromobility - Green-700
              'rgba(245, 158, 11, 1)',
              'rgba(139, 92, 246, 1)',
              'rgba(236, 72, 153, 1)',
            ],
        borderWidth: 2,
      },
    ],
  };

  const contributionsOptions = {
    type: 'bar' as const,
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    layout: {
      padding: {
        top: 10, // Reduced to minimize white space
        bottom: 12, // Reduced to minimize white space
        left: 24,
        right: 24,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.x;
            const percentage = totalReduction > 0 ? ((value / totalReduction) * 100).toFixed(1) : '0';
            return [
              `Reduction: ${value.toLocaleString()} MT CO₂e`,
              `Contribution: ${percentage}% of total`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'GHG Reduction (Metric Tons CO₂e)',
        },
        ticks: {
          callback: (value: any) => Number(value).toLocaleString(),
        },
      },
      y: {
        ticks: {
          autoSkip: false,
          padding: 6,
        },
        grid: {
          display: false,
        },
      },
    },
  };

  return chartType === 'line' ? (
    /* Line Chart */
    <div className="chart-section">
      <ChartRendererSupabase
        chartData={chartData}
        chartOptions={chartOptions}
        height="400px"
        width="100%"
      />
    </div>
  ) : chartType === 'contributions' ? (
    /* Contributions Chart */
    <div className="chart-section">
      <ChartRendererSupabase
        chartData={contributionsData}
        chartOptions={contributionsOptions}
        height="400px"
      />
    </div>
  ) : chartType === 'stacked' ? (
    /* Stacked Area Chart */
    <div className="chart-section">
      <ChartRendererSupabase
        chartData={stackedChartData || { labels: [], datasets: [] }}
        chartOptions={stackedChartOptions}
        height="400px"
        width="100%"
      />
    </div>
  ) : (
    /* Forecast Chart */
    <div className="chart-section">
      <ChartRendererSupabase
        chartData={forecastChartData || { labels: [], datasets: [] }}
        chartOptions={forecastChartOptions}
        height="400px"
      />
    </div>
  );
};

export default PolicyImpactChart;
